/**
 * The timer palette — ONE source of truth for every colour in the timer family.
 *
 * ── WHY THIS FILE IS NOW THE WHOLE PALETTE (2026-08-10) ─────────────────────
 *
 * It used to be a 7-key hand-copied MIRROR of a 23-key `TC` object living
 * inside UnifiedWorkoutTimer, kept in lockstep by a docstring and nothing else.
 * Meanwhile EMOMTimerDisplay and AMRAPTimerDisplay carried a THIRD ramp —
 * duplicated verbatim between them, neutral grey where the timer is
 * blue-charcoal — under the comment "matches UnifiedWorkoutTimer", which was
 * false on every single token.
 *
 * Three ramps, two of them agreeing by luck. The rollout doc excluded the timer
 * from Daylight as "already one coherent dark instrument"; it was three. So the
 * mirror became the original: the timer and both clock-driven displays now
 * import from here, and the lockstep promise is structural instead of manual.
 *
 * This module imports nothing but the shared colour constants, so the old cycle
 * concern (sheets importing the timer that imports them) does not arise.
 *
 * ── CONTRAST IS MEASURED, NOT ASSUMED ───────────────────────────────────────
 *
 * The timer draws on THREE grounds — bgPrimary, cardBg, bgTertiary — so ink has
 * to clear AA on the worst of them, not the flattering one. Two tokens did not:
 *
 *   textTertiary  #6B7280 → 3.90 / 3.30 / 3.03   at 11–13px, across 15 sites
 *   accentPrimary #1F6C4F → 2.98 / 2.52 / 2.31   used as ink AND as the dots
 *
 * `textTertiary` is lifted to #8B92A3 (6.05 / 5.12 / 4.69). The stage dots get
 * their own named tokens instead of borrowing a BUTTON FILL — accentPrimary
 * stays exactly where it is, because white on it is 6.33:1 and the CTA family
 * depends on that. A fill and a mark are different jobs.
 *
 * Every ratio here is re-computed from these values by
 * tests/gate/test_timer_one_palette.py, so a re-tint is re-measured rather than
 * re-trusted. Nothing in that gate pins a hex except the two literals
 * test_log_drawer_ux_phase4 also asserts live in this file.
 */
import { CoachCedarColors } from '../../utils/styleConverter';

export const TIMER_DARK = {
  // ── grounds ───────────────────────────────────────────────────────────────
  bgPrimary: '#0F1118',
  bgSecondary: '#1A1D28',
  bgTertiary: '#252836',
  /** Exercise / phase card fill. Was BOTH `cardBg` and `bgCard` in the old TC. */
  cardBg: '#1E2130',

  // ── ink ───────────────────────────────────────────────────────────────────
  textPrimary: '#FFFFFF',
  textSecondary: '#B8BCC8',
  /**
   * Lifted from #6B7280 (2026-08-10). The old value measured 3.90 / 3.30 / 3.03
   * across the timer's three grounds — below AA at the 11–13px it is used at,
   * on 15 live sites. One of them already carried a comment acknowledging it.
   */
  textTertiary: '#8B92A3',
  /** Cedar mint. Was BOTH `textCedar` and `accentSecondary`. */
  textCedar: '#5FA287',
  /** Italic form-tip copy. 9.24:1 on the canvas. */
  textFormTip: '#88CCAA',
  /** Placeholder / disabled text on dark surfaces. */
  textFaint: 'rgba(255,255,255,0.35)',

  // ── marks (non-text: dots, rules, indicators — WCAG 1.4.11, floor 3:1) ────
  /**
   * The stage indicator used accentPrimary #1F6C4F — 2.98:1, below even the
   * non-text floor. The timer's primary "where am I" affordance was the least
   * visible thing on screen. These are marks, so they get mark tokens.
   */
  /**
   * Deliberately NOT the same value as `textCedar`, though it started that way.
   * A completed step should read as behind you — quieter than mint ink and much
   * quieter than the live step — so it gets its own damped mint at 4.78:1
   * rather than borrowing one. Same value in two roles is how the old TC ended
   * up with 23 keys for 18 colours.
   */
  dotDone: '#4E8C74',   // 4.78:1 on the canvas
  dotActive: '#3BD49B', // 9.95:1 — the live step is the brightest thing in the row

  // ── hairlines ─────────────────────────────────────────────────────────────
  /** Card + control edges. Was BOTH `cardBorder` and `borderMedium`. */
  borderMedium: '#2D3142',
  /** Quieter edges on exercise UI. In the old TC this ALSO doubled as bgTertiary. */
  borderSubtle: '#22242F',
  /** Hairlines on sheets launched from the timer. */
  border: 'rgba(255,255,255,0.10)',

  // ── accents ───────────────────────────────────────────────────────────────
  /**
   * A BUTTON FILL, deliberately not lifted. Its PRESS is `buttonPrimaryPressed`
   * below, which deepens rather than brightens. It measures 2.98:1 against the
   * canvas and that is fine — nothing reads a fill as text. What matters is the
   * label on it: white on #1F6C4F is 6.33:1. Lifting this "for contrast" would
   * break the whole CTA family. Was BOTH `accentPrimary` and `buttonPrimary`.
   */
  accentPrimary: '#1F6C4F',
  accentSuccess: '#2E7D32',
  accentWarning: '#F59E0B',
  accentDanger: '#DC2626',

  // ── pressed states ────────────────────────────────────────────────────────
  // PRESS BY DEEPENING, not brightening (CTA family audit, 2026-08-16).
  // This token is the press for the timer's ENTIRE control row (~8 call
  // sites: pause/resume, lap, finish, the exercise floater, the strength
  // nav). It was `thriveDeep` (#2BAE82) under `ctrlBtnText: '#FFF'` —
  // 2.81:1, below the 4.5:1 AA floor for exactly as long as the finger was
  // down, which is the one moment the label is being read. Deepening keeps
  // the press just as visible (a 1.94 luminance step vs thriveDeep's 2.26)
  // and takes white to 12.32:1 instead.
  buttonPrimaryPressed: CoachCedarColors.cedarDark,
  accentWarningPressed: '#FBBF24',
  accentDangerPressed: '#EF4444',
  accentDangerDeep: '#B71C1C',

  // ── the timer screen's own one-offs (dark-mode P9) ────────────────────────
  // Each was written out at its one site in UnifiedWorkoutTimer before the
  // timer moved to the theme; the timer is the same picture in both themes,
  // so each is a role here, at the value it always had.
  /** What a dialog lays over the timer (at 70%), and the full-screen animation (at 85%). */
  scrim: '#000000',
  /** The words of a destructive dialog button, on its red outline. */
  dangerInk: '#F08A8A',
  /** The mobility card's Auto / Manual icon that is not chosen. */
  iconMuted: '#AAAAAA',
  /** "Acquiring GPS Signal…" and its spinner. */
  signalSearching: '#FFA500',
  /** The edge (and, at 12%, the fill) of the banner that asks to re-pair a sensor. */
  bannerWarning: '#FFB84C',
  /** A sport session's REST badge (at 15% inside 35%). */
  restBadge: '#94A3B8',
  /** The floating control bar's ground where the phone cannot blur (at 95%). */
  floaterFallback: '#151821',
  /** A coaching tip's colour, by its kind: pace, form (and any other), breathing. Motivation is `accentWarning`. */
  guidancePace: CoachCedarColors.cedarStream,
  guidanceForm: '#22C55E',
  guidanceBreathing: '#A855F7',
} as const;

/**
 * Back-compat aliases for FOUR of the old `TC`'s five exact-duplicate pairs
 * (23 keys for 18 values, with BOTH members of every pair live at call sites).
 * The fifth pair — borderSubtle doubling as bgTertiary — was deliberately
 * DISSOLVED, not aliased: borderSubtle got its own darker value above, since a
 * border identical to its ground was invisible by construction.
 *
 * They are aliases, not copies — `TIMER_DARK` above holds each value once, so
 * these cannot drift from it. Kept so this change stays a palette move rather
 * than a 200-call-site rename; the renames can follow separately and safely.
 */
export const TIMER_ALIASES = {
  bgCard: TIMER_DARK.cardBg,
  cardBorder: TIMER_DARK.borderMedium,
  accentSecondary: TIMER_DARK.textCedar,
  buttonPrimary: TIMER_DARK.accentPrimary,
} as const;

/** The timer's full token set: the palette plus its historical aliases. */
export const TC_TOKENS = { ...TIMER_DARK, ...TIMER_ALIASES } as const;
